import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  clearTablesWithTran,
  getMultipleEntriesFromDB,
} from "../../helpers/db";
import {
  manageErrorLogs,
  checkCatchMessage,
  getUserDatailsStatus,
} from "../../helpers/commonFunction";
import { getLatestDataFromDB } from "../../helpers/offlineAPIs";
import { routes, SYNC_TYPE } from "../../helpers/constants";
import { useFetchAndSyncData } from "../../hooks";
import { OfflineModal, ConfirmationModal } from "../../components/Modals";
import { reqToGetUserDetailsFromIDB } from "../../store/slice/indexedDBSlice";
import { insertUserDataInDB } from "../../helpers/offlineDBQuery";

const Sync = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData: storeUser, isUserDetailsIDBChecked } = useSelector(
    (state) => ({
      userData: state.indexedDBReducer?.userData,
      isUserDetailsIDBChecked: state.indexedDBReducer?.isUserDetailsIDBChecked,
    }),
    shallowEqual,
  );

  const [userData, setUserData] = useState(null);
  const [isOfflineData, setIsOfflineData] = useState(true);

  useEffect(() => {
    checkDataInLocalDB();
  }, []);

  useEffect(() => {
    if (!isUserDetailsIDBChecked) {
      dispatch(reqToGetUserDetailsFromIDB({}));
    }
  }, [isUserDetailsIDBChecked]);

  const {
    syncMessage,
    offlineModal,
    confirmationModal,
    handleOfflineModal,
    handleConfirmModal,
    uploadAndFetchData,
  } = useFetchAndSyncData();

  const checkDataInLocalDB = async () => {
    try {
      const fetchData = await getMultipleEntriesFromDB([
        "userData",
        "addedRestaurantDetails",
        "addedRestaurantTaskNotes",
      ]);
      if (!fetchData || Object.keys(fetchData)?.length === 0) return;
      const { userData, addedRestaurantDetails, addedRestaurantTaskNotes } =
        fetchData;
      const user = storeUser?.user_id
        ? storeUser
        : Array.isArray(userData) && userData?.length > 0
          ? userData[0]
          : null;
      setUserData(user);
      if (
        addedRestaurantDetails?.length > 0 ||
        addedRestaurantTaskNotes?.length > 0
      ) {
        setIsOfflineData(true);
      } else {
        checkTimeInOut("autoSync", user);
      }
    } catch (error) {
      manageErrorLogs(error);
      checkCatchMessage(error?.message || "Something went wrong in sync!");
    }
  };

  const checkTimeInOut = async (type, user) => {
    const newUser = user ? user : userData;
    const { getOldTime, getNewTime, isNextMonth } =
      getUserDatailsStatus(newUser);
    if (getOldTime !== getNewTime) {
      const newUserData = {
        ...newUser,
        time_in: false,
        timein_date: null,
        timeout_date: null,
        last_updated_time: new Date(),
      };
      if (type === "autoSync") {
        await clearTablesWithTran("timeInOut");
        const isAllDataUpdated = await getLatestDataFromDB(
          isNextMonth ? SYNC_TYPE.ISNMONTH : SYNC_TYPE.FINALUPLOAD,
          dispatch,
          [newUserData],
        );
        if (isAllDataUpdated) {
          setIsOfflineData(false);
        }
      } else {
        await insertUserDataInDB(newUserData, dispatch);
        setIsOfflineData(false);
      }
    } else {
      setIsOfflineData(false);
    }
  };

  const handleSyncData = async () => {
    const { isNextMonth } = getUserDatailsStatus(userData);
    await uploadAndFetchData(
      isNextMonth ? SYNC_TYPE.ISNMONTH : SYNC_TYPE.FINALUPLOAD,
      checkTimeInOut,
    );
  };

  return (
    <>
      <div className="tc-body pb-5">
        <div className="tc-content-wrapper tc-sync-content-wrapper mt-3">
          <div className="tc-sync-top-content">
            <h5>
              Please tap the <span>SYNC</span> button to make sure all your data
              are uploaded. Thank you!
            </h5>
            <div className="mt-4 text-center">
              <Button
                variant="primary"
                type="button"
                className="btn-min"
                onClick={() => handleSyncData()}
                disabled={!isOfflineData}
              >
                SYNC
              </Button>
            </div>
          </div>
          <span className="tc-sync-center-content"></span>
          <div className="tc-sync-bottom-content">
            <span>STATUS</span>
            <div className="tc-sync-data-status mt-3">
              <span></span>
              <div className="tc-sync-data-status-content">
                {isOfflineData ? (
                  <div className="color-red">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 64 64"
                      className="tc-failure-checkmark toggle mb-2"
                    >
                      <circle
                        className="tc-failure-checkmark-circle"
                        cx="32"
                        cy="32"
                        r="30"
                        fill="none"
                      />
                      <line
                        className="tc-failure-left-check"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                        x1="19.271"
                        y1="19.521"
                        x2="44.729"
                        y2="44.979"
                      />
                      <line
                        className="tc-failure-right-check"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                        x1="44.729"
                        y1="19.521"
                        x2="19.271"
                        y2="44.979"
                      />
                    </svg>
                    Not all data has been uploaded and downloaded!
                  </div>
                ) : (
                  <div className="color-green">
                    <svg
                      className="tc-success-checkmark mb-2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 52 52"
                    >
                      <circle
                        className="tc-success-checkmark-circle"
                        cx="26"
                        cy="26"
                        r="25"
                        fill="none"
                      />
                      <path
                        className="tc-success-checkmark-check"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.1 27.2l7.1 7.2 16.7-16.8"
                      />
                    </svg>
                    All data are successfully uploaded and downloaded!
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 text-center">
              {!isOfflineData && (
                <Button
                  variant="primary"
                  type="button"
                  className="btn-min"
                  onClick={() => navigate(routes.dashboard)}
                  disabled={isOfflineData}
                >
                  DASHBOARD
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="tc-footer-blank"></div>
      </div>
      {offlineModal && (
        <OfflineModal show={offlineModal} onHide={() => handleOfflineModal()} />
      )}
      {confirmationModal && (
        <ConfirmationModal
          show={confirmationModal}
          onHide={handleConfirmModal}
          title="Syncing Data"
          subTitle="Failed to sync data!"
          description="Please resync the data!"
          cancel="Cancel"
          save="Syncing"
          errors={syncMessage}
          onSubmit={handleSyncData}
        />
      )}
    </>
  );
};

export default Sync;
